import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../../contexts/CartContext';
import { useAuth } from '../../contexts/AuthContext';
import {
  db, collection, getDocs, query, where
} from '../../firebase/config';
import {
  ShoppingBag, Search, Plus, Heart, User, MapPin, Package
} from 'lucide-react';

const ShopPage = () => {
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { user } = useAuth();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [category, setCategory] = useState('All');
  const [toast, setToast] = useState(null);

  const categories = ['All', 'Skincare', 'Makeup', 'Haircare', 'Bodycare', 'Tools', 'Other'];

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2000);
  };

  // ---------- Load products + pros ----------
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const [productsSnap, prosSnap] = await Promise.all([
          getDocs(collection(db, 'products')),
          getDocs(query(collection(db, 'users'), where('role', '==', 'professional'))),
        ]);

        // Build pro lookup map
        const prosMap = {};
        prosSnap.docs.forEach(d => {
          prosMap[d.id] = { id: d.id, ...d.data() };
        });

        // Products — filter active + not mine
        const productsData = productsSnap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(p => p.active !== false)
          .filter(p => !user?.uid || p.professionalId !== user.uid)
          .map(p => ({
            ...p,
            pro: prosMap[p.professionalId] || null,
          }));

        setProducts(productsData);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [user?.uid]);

  // ---------- Filter + sort ----------
  const filteredProducts = useMemo(() => {
    let list = [...products];

    if (category !== 'All') {
      list = list.filter(p => p.category === category);
    }

    if (searchQuery.trim()) {
      const s = searchQuery.toLowerCase();
      list = list.filter(p =>
        (p.name || '').toLowerCase().includes(s) ||
        (p.brand || '').toLowerCase().includes(s) ||
        (p.description || '').toLowerCase().includes(s) ||
        (p.category || '').toLowerCase().includes(s) ||
        (p.pro?.businessName || '').toLowerCase().includes(s) ||
        (p.pro?.name || '').toLowerCase().includes(s)
      );
    }

    // Newest first
    list.sort((a, b) => {
      const aT = a.createdAt?.toMillis?.() ?? new Date(a.createdAt || 0).getTime();
      const bT = b.createdAt?.toMillis?.() ?? new Date(b.createdAt || 0).getTime();
      return bT - aT;
    });

    return list;
  }, [products, category, searchQuery]);

  // ---------- Media helpers ----------
  const getCover = (product) => {
    if (Array.isArray(product.media) && product.media.length > 0) return product.media[0];
    if (product.media?.url) return product.media;
    if (product.mediaLegacy?.url) return product.mediaLegacy;
    if (product.image) return { url: product.image, type: 'image' };
    return null;
  };

  const getMediaCount = (product) => {
    if (Array.isArray(product.media)) return product.media.length;
    if (product.media?.url || product.mediaLegacy?.url || product.image) return 1;
    return 0;
  };

  // ---------- Add to cart ----------
  const handleAddToCart = async (e, product) => {
    e?.stopPropagation();
    try {
      await addToCart(product, 1);
      showToast(`Added ${product.name} to cart`);
    } catch (err) {
      console.error('Add to cart failed:', err);
      showToast('Failed to add', 'error');
    }
  };

  // ---------- Product click ----------
  const handleProductClick = (product) => {
    if (!product.pro?.id) return;
    navigate(`/professional/${product.pro.id}`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-[#EFA9B6] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-serif font-bold text-gray-900">Shop</h1>
        <p className="text-gray-500 mt-1">
          {products.length > 0
            ? `${products.length} product${products.length === 1 ? '' : 's'} available`
            : 'Discover premium beauty products'}
        </p>
      </div>

      {/* Search + category */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search products, brands..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3.5 bg-white border border-gray-100 rounded-2xl text-sm focus:outline-none focus:border-[#EFA9B6]"
          />
        </div>
        <div className="flex gap-2 overflow-x-auto hide-scrollbar">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                category === cat
                  ? 'bg-[#010101] text-white'
                  : 'bg-white text-gray-600 border border-gray-100 hover:bg-gray-50'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filteredProducts.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-gray-50">
          <div className="w-20 h-20 bg-[#FFF5F6] rounded-full flex items-center justify-center mx-auto mb-4">
            <ShoppingBag size={32} className="text-[#EFA9B6]" />
          </div>
          <h3 className="text-lg font-serif font-bold text-gray-900 mb-2">
            {searchQuery || category !== 'All' ? 'No results' : 'No products yet'}
          </h3>
          <p className="text-gray-400 text-sm">
            {searchQuery || category !== 'All'
              ? 'Try a different search term or category'
              : 'Check back soon — pros are adding new products every day.'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProducts.map((product) => {
            const cover = getCover(product);
            const mediaCount = getMediaCount(product);
            const outOfStock = product.stock === 0;
            const lowStock = product.stock > 0 && product.stock < 5;

            return (
              <div
                key={product.id}
                onClick={() => handleProductClick(product)}
                className="bg-white rounded-2xl p-4 shadow-sm border border-gray-50 hover:shadow-md hover:border-[#EFA9B6] transition-all cursor-pointer group"
              >
                {/* Image */}
                <div className="relative">
                  <div className="h-48 rounded-xl bg-gradient-to-b from-[#F8F9FA] to-white flex items-center justify-center mb-4 overflow-hidden">
                    {cover ? (
                      cover.type === 'video' ? (
                        <video src={cover.url} className="h-40 object-contain" muted playsInline />
                      ) : (
                        <img
                          src={cover.url}
                          className="h-40 object-contain group-hover:scale-105 transition-transform duration-300"
                          alt={product.name}
                        />
                      )
                    ) : (
                      <ShoppingBag size={48} className="text-gray-300" />
                    )}
                  </div>

                  {/* Multi-photo badge */}
                  {mediaCount > 1 && (
                    <div className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                      +{mediaCount - 1}
                    </div>
                  )}

                  {/* Stock badge */}
                  {(lowStock || outOfStock) && (
                    <div className={`absolute top-2 left-2 text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${
                      outOfStock
                        ? 'bg-gray-900 text-white'
                        : 'bg-amber-50 text-amber-600'
                    }`}>
                      {outOfStock ? 'Sold out' : `Only ${product.stock} left`}
                    </div>
                  )}

                  {/* Favorites */}
                  <button
                    onClick={(e) => e.stopPropagation()}
                    className="absolute top-2 right-2 p-2 bg-white rounded-full shadow-sm hover:bg-[#FFF5F6] transition-colors opacity-0 group-hover:opacity-100"
                  >
                    <Heart size={18} className="text-gray-400 hover:text-[#EFA9B6]" />
                  </button>
                </div>

                {/* Title + pro */}
                <div className="flex items-start justify-between mb-1 gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 truncate">
                      {product.name}
                    </h3>

                    {/* Pro chip */}
                    {product.pro && (
                      <div className="flex items-center gap-1.5 mt-1">
                        <div className="w-4 h-4 rounded-full bg-[#FFF5F6] flex items-center justify-center overflow-hidden shrink-0">
                          {product.pro.profileImage || product.pro.coverImage ? (
                            <img
                              src={product.pro.profileImage || product.pro.coverImage}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <User size={8} className="text-[#EFA9B6]" />
                          )}
                        </div>
                        <p className="text-[11px] text-gray-500 truncate">
                          {product.pro.businessName || product.pro.name}
                        </p>
                      </div>
                    )}

                    {/* Brand */}
                    {product.brand && (
                      <p className="text-xs text-gray-400 truncate mt-0.5">{product.brand}</p>
                    )}
                  </div>
                  <span className="text-lg font-bold text-gray-900 shrink-0">
                    R{Number(product.price || 0).toFixed(2)}
                  </span>
                </div>

                {/* Meta row */}
                <div className="flex items-center gap-3 text-[11px] text-gray-400 mt-2">
                  {product.stock !== undefined && (
                    <span className={`flex items-center gap-1 ${lowStock ? 'text-amber-600 font-bold' : ''}`}>
                      <Package size={11} />
                      {outOfStock ? 'Sold out' : `${product.stock} in stock`}
                    </span>
                  )}
                  {product.pro?.location && (
                    <span className="flex items-center gap-1 truncate">
                      <MapPin size={11} />
                      {product.pro.location}
                    </span>
                  )}
                </div>

                {/* Description */}
                {product.description && (
                  <p className="text-xs text-gray-400 mt-2 line-clamp-2">{product.description}</p>
                )}

                {/* CTA */}
                <button
                  onClick={(e) => handleAddToCart(e, product)}
                  disabled={outOfStock}
                  className={`w-full mt-3 py-2.5 text-sm font-bold rounded-xl transition-colors flex items-center justify-center gap-2 ${
                    outOfStock
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-[#010101] text-white hover:bg-black/90'
                  }`}
                >
                  <Plus size={16} />
                  {outOfStock ? 'Sold Out' : 'Add to Cart'}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[70]">
          <div className={`px-5 py-3 rounded-full shadow-lg text-sm font-bold ${
            toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-[#010101] text-white'
          }`}>
            {toast.msg}
          </div>
        </div>
      )}

      <style dangerouslySetInnerHTML={{__html: `
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}} />
    </div>
  );
};

export default ShopPage;